import { makeAutoObservable, observable } from "mobx";
import SequencesManager, { SequencesFile } from 'react-sdk/SequencesManager';
import Texts from 'react-sdk/Texts';
import SoundPlayer from "./SoundPlayer";
import AppState from "./AppState";

const youtubeParams = "?iv_load_policy=3&MODEStbranding=1&showinfo=0&rel=0&control=1" // https://developers.google.com/youtube/player_parameters?hl=fr#Parameters

export const nodeDialConfig = {
  attitudeTimings: "attitudeTimings",
  audio : "audio",
  character : "character",
  text : "text",
  textTimings : "textTimings"
}

class PresentationChapter {

  nodes = []
  id = null
  title = null
  sizeSum = 0

  constructor(id, sizeSum) {
    this.id = id
    this.title = Texts.get(id + "-title")
    this.sizeSums = sizeSum
  }
}

class PresentationNode {
  chapterIndex = -1
  indexInchapter = -1
  globalIndex = -1

  element = null
  type = null

  dialData = null
  showCharacter = false
  otherCharacters = []
  
  background = null
  content = null
  content_alt_text = null

  constructor(elem, indexInchapter, globalIndex, chapterIndex) {

    this.element = elem

    this.chapterIndex = chapterIndex
    this.indexInchapter = indexInchapter
    this.globalIndex = globalIndex    
  
    this.type = elem.Type
    
    if(this.setupDialogue()) this.setupContent()
  }

  setupDialogue() {

    if(!this.element.raw.Character) {
      console.log(this.element)
      console.error("character for " + this.element.id + " is NULL")
      return false
    }

    this.showCharacter = this.element.raw.showCharacter

    this.dialData = {
      text: this.element.raw["text_" + AppState.language],
      character: this.element.raw.Character,
      vue: this.element.raw.view ? this.element.raw.view : "standard",
      audio: AppState.files_root + window.CONFIG.speech.audios_root + this.element.raw["audio_" + AppState.language],
      textTimings: this.element.raw["textTimings_" + AppState.language]? this.element.raw["textTimings_" + AppState.language] : "10000:10000",
      attitudeTimings: this.element.raw["attitudeTimings_" + AppState.language]
    }

    return true
  }

  setupContent() {
    this.background = AppState.files_root + window.CONFIG.background_root + (this.element.raw.background? this.element.raw.background : "default.png")
    this.type = this.element.raw.Type
    
    if(this.type != null && !this.element.raw["content_" + AppState.language]) {
      console.log(this.element)
      console.warn("content for " + this.element.id + " is NULL")
      return
    }

    switch(this.type) {
      case "image": {
        if(this.element.raw["content_" + AppState.language].includes("http")) this.content = this.element.raw["content_" + AppState.language]
        else this.content = AppState.files_root + window.CONFIG.image_root + this.element.raw["content_" + AppState.language]
        break;
      }
      case "video": {
        this.content = AppState.files_root + window.CONFIG.video_root + this.element.raw["content_" + AppState.language]
        break;
      }
      case "youtube": {
        this.content = this.element.raw["content_" + AppState.language] + youtubeParams
        break;
      }
      case "text": {
        this.content = this.element.raw["content_" + AppState.language]
        break;
      }
      case "download": {
        this.content = this.element.raw["content_" + AppState.language]
        break;
      }
      default: {
        this.content = null
        break;
      }
    }
    this.content_alt_text = this.element.raw["content_alt_text_" + AppState.language] 
  }

  isCharacterDisplayed(charId) {
    if(this.dialData.character === charId && this.showCharacter) return true
    if(this.secondCharacter === charId) return true
    return false
  }
}

export const MODES = {
  NORMAL: "NORMAL",
  REWIND: "REWIND"
}

export const REWINDMODES = {
  LASTSEEN: "LASTSEEN", // rewind to see the last seen
  PREVIOUS: "PREVIOUS" // rewind to see the previous one in timeLine
}

class PresentationHelper {

  presentationSequence = null
  presentationSequenceFile = null
  started = false

  chapters = []
  currentNode = null
  audios = []
  characters = []

  vuesParameters = []

  mode = MODES.NORMAL
  rewindMode = REWINDMODES.PREVIOUS

  constructor() {

  }

  init(sequenceFile, root)
  {
    let sequence = SequencesFile.createFromDataFile("presentation", sequenceFile)
    SequencesManager.addFile(sequence)
    this.presentationSequenceFile = SequencesManager.findFile("presentation")
    this.parseSequence(this.presentationSequenceFile, root)
   
    makeAutoObservable(this, {
      currentNode: observable
    })
  }

  initVues(file) {
    file.forEach(vue => {

      let characters = []

      if(vue.rightCharacters) {
        if(vue.rightCharacters.includes("_")) characters = vue.rightCharacters.split("_")
        else characters = [vue.rightCharacters]
      }

      this.vuesParameters.push({ id: vue.id, rightCharacters: characters})
    })
    this.vuesParameters = file
  }


  isCurrentCharacterRight() {
    let vue = this.vuesParameters.find((vue) => vue.id === this.currentNode.dialData.vue)
    if(vue && vue.rightCharacters)
      return vue.rightCharacters.includes(this.currentNode.dialData.character)
    return false
  }

  parseSequence(sequenceFile, root) {
    let sizeCount = 0
    let index = 0
    let newNode = null

    sequenceFile.elements.forEach(elem => {

      if(this.chapters.length === 0)
      {
        this.chapters.push(new PresentationChapter(elem.SequenceId, 0))
        newNode = new PresentationNode(elem, 0, 0, 0)
       
        if(newNode.dialData.character && this.characters.includes(newNode.dialData.character) === false) this.characters.push(newNode.dialData.character)
       
        this.chapters[index].nodes.push(newNode)
      }
      else if(this.chapters[index].id === elem.SequenceId) // element contained in a chapter
      {
        newNode = new PresentationNode(elem, this.chapters[index].nodes.length, sizeCount + this.chapters[index].nodes.length, index)
        if(newNode.dialData.character && this.characters.includes(newNode.dialData.character) === false) this.characters.push(newNode.dialData.character)
        this.chapters[index].nodes.push(newNode)
      }
      else
      {
        sizeCount += this.chapters[index].nodes.length

        this.chapters.push(new PresentationChapter(elem.SequenceId, sizeCount))
        ++index
        
        newNode = new PresentationNode(elem, this.chapters[index].nodes.length, this.chapters[index].nodes.length, index)
        if(newNode.dialData.character && this.characters.includes(newNode.dialData.character) === false) this.characters.push(newNode.dialData.character)
        this.chapters[index].nodes.push(newNode)
      }

      if(elem.raw["audio_" + AppState.language]) 
        this.audios.push(root + window.CONFIG.speech.audios_root + elem.raw["audio_" + AppState.language])
  

    })

    this.audios.push(root + window.CONFIG.speech.audios_root + "silence")
  }

  startPresentation() {
    this.presentationSequence = SequencesManager.playSequence("presentation", "chapitre1")
    this.switchElement(0, 0);

    this.started = true
  }

  handleNext()
  {
    // this.mode = MODES.NORMAL

    if(this.currentNode.indexInchapter < this.chapters[this.currentNode.chapterIndex].nodes.length - 1) // next node in the same chapter
    {
      this.switchElement(this.currentNode.chapterIndex, this.currentNode.indexInchapter + 1)
    }
    else if(this.currentNode.chapterIndex < this.chapters.length - 1) // first node of the next chapter
    {
      this.switchElement(this.currentNode.chapterIndex + 1, 0)
    }
    else // last node
    {
      // AppState.quit()
    }
  }

  getCharacterList() {
    return this.characters
  }

  getCurrentTitle() {
    return this.chapters[this.currentNode.chapterIndex].title
  }

  getTitles() {
    return this.chapters.map(chap => chap.title)
  }

  getAudiosArray() {
    return this.audios
  }

  getFirstSpeaker() {
    return this.chapters[0].nodes[0].dialData.character
  }

  handleRewind()
  {
    // this.mode = MODES.REWIND

    if(this.rewindMode === REWINDMODES.LASTSEEN)
    {
      let previous = this.presentationSequence.history[this.presentationSequence.history.length - 2]
      let node = this.retrieveNodeFromSequenceElement(previous)
  
      this.switchElement(node.chapterIndex, node.indexInchapter)
  
      this.presentationSequence.history.pop() // cleaning history
      this.presentationSequence.history.pop() // cleaning history  
    }
    else
    {
      if(this.currentNode.indexInchapter > 0) {
        // same chapter
       
        this.switchElement(this.currentNode.chapterIndex, this.currentNode.indexInchapter - 1)
      }
      else if(this.currentNode.chapterIndex > 0) {
        // previous chapter
       
        this.switchElement(this.currentNode.chapterIndex - 1, this.chapters[this.currentNode.chapterIndex - 1].nodes.length - 1)
      }
      else {
        // first element
      }
    }
  }

  handleTimelineClick(chapterIndex, ind) {
    // this.mode = MODES.REWIND
    
    this.handleGoToElement(chapterIndex, ind)
  }

  retrieveNodeFromSequenceElement(element) 
  {
    let chapter = null

    this.chapters.every(c => {
      if(c.id === element.SequenceId)
      {
        chapter = c;
        return false // break
      }
      return true;
    })

    let node = null
    chapter.nodes.every(n => {
      if(n.element === element)
      {
        node = n;
        return false // break
      }
      return true;

    })
    return node
  }

  handleGotToSequence(chapterIndex)
  {
    this.switchElement(chapterIndex, 0)
  }

  handleGoToElement(chapterIndex, ind)
  {
    if(this.currentNode.chapterIndex === chapterIndex && this.currentNode.indexInchapter === ind) return;

    this.switchElement(chapterIndex, ind)
  }

  switchElement(chapterIndex, ind)
  {
    let nextNode = this.chapters[chapterIndex].nodes[ind]
    this.presentationSequence.switchToCurrent(nextNode.element)
    this.currentNode = nextNode
  }

  rewindToLastSeen(active)
  {
    this.rewindMode = active? REWINDMODES.LASTSEEN : REWINDMODES.PREVIOUS;
  }

  hasHistory()
  {
    return this.presentationSequence?.history && this.presentationSequence.history.length > 1
  }

}

export default new PresentationHelper()